/**
 * Pure per-item forge helpers shared by the forge actions, the v2→v3 save migration and the UI:
 * default forge state, masterwork ceilings, salvage yields. No RNG, no profile access.
 */
import {
  GRADE_CRAFT_ILVL,
  MASTERWORK_CEILING,
  SALVAGE_RARITY,
  gradeForIlvl,
} from './forge.constants';
import { BASE_ILVL, MASTERWORK_MAX } from './items';
import type { Gem, Item, ItemForgeState, Rarity, ResourceBundle, ScaleGrade } from './types';
import { RARITIES, SCALE_GRADES } from './types';

export function emptyItemForge(): ItemForgeState {
  return { steps: 0, fails: 0, resonance: 0, invested: 0 };
}

/** The item's forge state (zeros when missing; never mutates the item). */
export function itemForge(item: Pick<Item, 'forge'>): ItemForgeState {
  return { ...emptyItemForge(), ...item.forge };
}

/**
 * Highest masterwork the item may be RAISED to: its rarity's ceiling (crafting.md §3.4), at most
 * MASTERWORK_MAX. Legacy masterwork above the ceiling is kept (never downgraded), it just can't
 * climb further.
 */
export function masterworkCeiling(item: Pick<Item, 'rarity'>): number {
  return Math.min(MASTERWORK_MAX, MASTERWORK_CEILING[item.rarity]);
}

/** Scale grade whose band pays for tempering this item's NEXT step (and its salvage refund). */
export function itemBandGrade(item: Pick<Item, 'ilvl'>): ScaleGrade {
  return gradeForIlvl(Math.max(item.ilvl, BASE_ILVL + 1));
}

/** Highest grade whose crafted item level is ≤ ilvl (Ashen at minimum). */
export function gradeForCraftIlvl(ilvl: number): ScaleGrade {
  let g: ScaleGrade = 'ashen';
  for (const x of SCALE_GRADES) if (GRADE_CRAFT_ILVL[x] <= ilvl) g = x;
  return g;
}

export const rarityIndex = (r: Rarity): number => RARITIES.indexOf(r);

/** Next rarity up (Ascend), never past Epic. */
export function rarityUp(r: Rarity): Rarity {
  const i = rarityIndex(r);
  return i >= rarityIndex('epic') ? r : RARITIES[i + 1]!;
}

/**
 * What salvaging gives back (crafting.md §3.8): Fangsteel 5 + 1 per 5 ilvl above 200 + rarity,
 * half the tempering Scales (rounded up) at the item's band grade, 1 Moltskin for Epic+, and the
 * Altar Sigil of a tier piece. Gems are returned separately (`salvageGems`).
 */
export function salvageYield(item: Item): ResourceBundle {
  const out: ResourceBundle = {
    fangsteel: 5 + Math.floor(Math.max(0, item.ilvl - BASE_ILVL) / 5) + SALVAGE_RARITY[item.rarity],
  };
  const invested = item.forge?.invested ?? 0;
  const scales = Math.ceil(invested / 2);
  if (scales > 0) out.scales = { [itemBandGrade(item)]: scales };
  if (rarityIndex(item.rarity) >= rarityIndex('epic')) out.moltskin = 1;
  if (item.setId) out.sigils = 1;
  return out;
}

export function salvageGems(item: Item): Gem[] {
  return item.gems.filter((g): g is Gem => g !== null).map((g) => ({ ...g }));
}

/**
 * Salvage of a pre-forge (save v2) item: as salvageYield, with a synthetic tempering investment
 * of 2 Scales per 5 ilvl above 200, paying at least 1 Scale (crafting.md §10).
 */
export function legacySalvageYield(item: Item): ResourceBundle {
  const synthetic = 2 * Math.floor(Math.max(0, item.ilvl - BASE_ILVL) / 5);
  const y = salvageYield({ ...item, forge: { ...emptyItemForge(), invested: synthetic } });
  y.scales = { [itemBandGrade(item)]: Math.max(1, Math.ceil(synthetic / 2)) };
  return y;
}
